/**
 * Document type → minimum tier mapping.
 * Prefer `minTier` from the document type record (set in admin); fall back to name patterns
 * for legacy types without minTier in the database.
 */
import { normalizePlanTier } from './planTierUtils';

const DOCUMENT_TYPE_MIN_TIER_RULES = [
	{ pattern: /honors\s*thesis/i, minTier: 'essayist' },
	{ pattern: /thesis|dissertation|master'?s|phd|doctoral/i, minTier: 'laureate' },
	{
		pattern: /\bprofessional(?:\s+(?:manuscript|paper))?|academic\s*research\s*[-–—]\s*professional/i,
		minTier: 'essayist'
	},
	{
		pattern: /\bstudent\b|academic\s*research\s*[-–—]\s*student|academic\s*paper/i,
		minTier: 'essentials'
	}
];

const TIER_RANK = { essentials: 0, essayist: 1, laureate: 2 };

const VALID_TIERS = new Set(['essentials', 'essayist', 'laureate']);

/**
 * @param {string | { name?: string; minTier?: string } | null | undefined} documentTypeOrName
 * @returns {'essentials' | 'essayist' | 'laureate'}
 */
export function getDocumentTypeMinTier(documentTypeOrName) {
	if (documentTypeOrName && typeof documentTypeOrName === 'object') {
		const tier = (documentTypeOrName.minTier || '').toLowerCase();
		const fromName = documentTypeOrName.name
			? getDocumentTypeMinTier(documentTypeOrName.name)
			: 'essentials';
		if (VALID_TIERS.has(tier)) {
			// Prefer the stricter of DB minTier vs name pattern (guards mis-tagged admin data)
			return (TIER_RANK[fromName] ?? 0) > (TIER_RANK[tier] ?? 0) ? fromName : tier;
		}
		return fromName;
	}

	const name = typeof documentTypeOrName === 'string' ? documentTypeOrName.trim() : '';
	if (!name) return 'essentials';

	for (const { pattern, minTier } of DOCUMENT_TYPE_MIN_TIER_RULES) {
		if (pattern.test(name)) {
			return minTier;
		}
	}
	return 'essentials';
}

/**
 * @param {string} planName - User's effective plan
 * @param {string | { name?: string; minTier?: string }} documentTypeOrName
 * @returns {boolean}
 */
export function isDocumentTypeAllowedForPlan(planName, documentTypeOrName) {
	if (!planName) return false;
	const plan = normalizePlanTier(planName);
	const minTier = getDocumentTypeMinTier(documentTypeOrName);
	const planRank = TIER_RANK[plan] ?? -1;
	const minRank = TIER_RANK[minTier] ?? 0;
	return planRank >= minRank;
}

/**
 * Essayist users should not see Laureate-only documents in lists.
 * @param {string} planName
 * @param {Array<{ documentType?: { name?: string; minTier?: string } }>} documents
 * @returns {Array}
 */
export function filterDocumentsVisibleForPlan(planName, documents) {
	if (!Array.isArray(documents)) return [];
	const plan = normalizePlanTier(planName);
	if (plan !== 'essayist') return documents;
	return documents.filter((doc) => {
		const docType = doc?.documentType;
		if (!docType?.name && !docType?.minTier) return true;
		return isDocumentTypeAllowedForPlan(planName, docType);
	});
}
